import { useEffect, useRef, type CSSProperties, type Ref } from 'react'
import type { BookingTranslationKey } from '../config'

// Visually-hidden text (inline, like components/ui/dialog.tsx: the widget's
// shadow root may not carry a utility `.sr-only`).
const srOnlyStyle: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
}

export type StickyBarState = 'initial' | 'loading' | 'quoted' | 'error'

interface StickyBarProps {
  state: StickyBarState
  /** Formatted no-dates rent, e.g. "$214.00" (WP: base/lowest rent). */
  startingPrice: string | null
  /** "Minimum stay: N nights" line under the starting price. */
  minStayText: string | null
  /** Formatted per-night price once quoted — the SAME figure as the in-page card, on
   *  the basis set by options.nightlyPriceBasis ('total' default, 'rent' = WP default). */
  nightPrice: string | null
  /** Pre-discount per-night price on the same basis, struck through when a discount
   *  applies and showNightlyStrikethrough is on; null otherwise. */
  nightPriceOriginal: string | null
  /** Formatted quote total (respects totalAfterTax). */
  totalPrice: string | null
  errorText: string | null
  /** "MMM d - MMM d" range; tapping it reopens the calendar modal. */
  datesText: string | null
  /** Whether the price line opens the price-details modal (WP: price_modal). */
  showPriceDetails: boolean
  buttonText: string
  buttonDisabled: boolean
  className?: string
  buttonClassName?: string
  onButtonClick: () => void
  onDatesClick: () => void
  onPriceClick: () => void
  /** #919: the quoted price line, so BookingForm can put focus back on it
   *  after the price-details modal closes — the bar is unmounted while that
   *  modal is open, so the modal itself cannot return focus to the element
   *  that opened it. */
  priceRef?: Ref<HTMLButtonElement>
  /** #1424: the sticky bar's own surrounding words ("Starting at", "/ night",
   *  "total", the loading skeleton's aria-label) — everything else here is
   *  already a pre-formatted number/string passed in from BookingForm. */
  _t: (key: BookingTranslationKey, defaultValue?: string) => string
}

// WP plugin icons (booking-widget.js updateStickyPrice/updateStickyDates),
// recolored via currentColor for dark mode.
const InfoIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M12 19c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7zm0-12.297c-2.913 0-5.297 2.384-5.297 5.297 0 2.914 2.384 5.297 5.297 5.297 2.914 0 5.297-2.383 5.297-5.297 0-2.913-2.383-5.297-5.297-5.297z" fill="currentColor" />
    <path d="M11.129 10.45h1.703v5.448h-1.703v-5.449zM12.87 8.822a.87.87 0 1 1-1.741 0 .87.87 0 0 1 1.74 0z" fill="currentColor" />
  </svg>
)

const CalendarEditIcon = () => (
  <svg width="18" height="18" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M8 4.8a.8.8 0 0 0-1.6 0v.8A2.4 2.4 0 0 0 4 8v9.6A2.4 2.4 0 0 0 6.4 20h3.2v-1.6H6.4a.8.8 0 0 1-.8-.8v-7.2h12.8V8A2.4 2.4 0 0 0 16 5.6v-.8a.8.8 0 0 0-1.6 0v.8H8v-.8z" fill="currentColor" />
    <path d="M8 12a.8.8 0 0 0 0 1.6h4a.8.8 0 0 0 0-1.6H8zM7.2 16.001a.8.8 0 0 1 .8-.8h1.6a.8.8 0 0 1 0 1.6H8a.8.8 0 0 1-.8-.8z" fill="currentColor" />
    <path fillRule="evenodd" clipRule="evenodd" d="M18.165 11.434a.8.8 0 0 0-1.131 0l-5.2 5.2a.8.8 0 0 0-.22.409l-.4 2a.8.8 0 0 0 .942.94l2-.4a.8.8 0 0 0 .409-.218l5.2-5.2a.8.8 0 0 0 0-1.131l-1.6-1.6zm-5.029 6.16L16.4 14.33l.469.468-3.263 3.263-.586.117.117-.585z" fill="currentColor" />
  </svg>
)

/**
 * Floating booking bar (WP plugin sticky-widget parity, #604). Fixed to the
 * bottom of the viewport on mobile, replacing the in-page widget. Four states
 * mirroring the WP jQuery show/hide flow: no dates (starting price + min
 * stay), quote loading (skeleton), quoted (per-night · total + info icon),
 * and quote error.
 */
export default function StickyBar({
  state,
  startingPrice,
  minStayText,
  nightPrice,
  nightPriceOriginal,
  totalPrice,
  errorText,
  datesText,
  showPriceDetails,
  buttonText,
  buttonDisabled,
  className,
  buttonClassName,
  onButtonClick,
  onDatesClick,
  onPriceClick,
  priceRef,
  _t,
}: StickyBarProps) {
  // While the bar overlays the viewport bottom, pad the host page by the
  // bar's height so end-of-page content (calendar legend, footer) can scroll
  // clear of it (#995). The bar's height changes with quote state, hence the
  // ResizeObserver; BookingForm unmounts the bar (modal open, desktop), and
  // the cleanup restores whatever padding the page had.
  const rootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const body = document.body
    const prev = body.style.paddingBottom
    // Remember what we last wrote: body padding is shared, and a second bar
    // (or the host page) may have written since. On unmount restore only if
    // the value is still ours — never clobber another writer's padding.
    let applied = prev
    const apply = () => {
      applied = `${Math.ceil(el.getBoundingClientRect().height)}px`
      body.style.paddingBottom = applied
    }
    apply()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      if (body.style.paddingBottom === applied) body.style.paddingBottom = prev
    }
  }, [])

  // What the persistent live region below says for each state (#919). Its
  // TEXT changing is what assistive tech announces — a skeleton that mounts
  // already holding an aria-label is a role-less div and announces nothing.
  const statusText =
    state === 'loading' ? _t('aria_loading_price')
    : state === 'quoted' && nightPrice ? `${nightPrice}${_t('unit_per_night')}, ${totalPrice ?? ''}${_t('unit_total')}`
    : state === 'error' ? (errorText ?? '')
    : ''

  const priceLine = (
    <>
      {nightPriceOriginal && <del className="rent-deleted">{nightPriceOriginal}</del>}
      <span className="rent-after-date rent-amount">{nightPrice}</span>
      <span>{_t('unit_per_night') + ' • '}</span>
      <span className="total-rent">{totalPrice}</span>
      <span>{_t('unit_total')}</span>
      <span className="booking-sticky-bar-info-icon"><InfoIcon /></span>
    </>
  )

  const datesLine = datesText && (
    <button type="button" className="booking-sticky-bar-dates" onClick={onDatesClick}>
      <span>{datesText}</span>
      <CalendarEditIcon />
    </button>
  )

  return (
    <div ref={rootRef} className={`booking-sticky-bar${className ? ` ${className}` : ''}`}>
      <div className="booking-sticky-bar-left">
        <span role="status" aria-live="polite" style={srOnlyStyle}>{statusText}</span>
        {state === 'initial' && (
          <>
            {startingPrice && (
              <div className="booking-sticky-bar-rent">
                <span>{_t('label_starting_at')}</span>
                <span className="rent-amount">{startingPrice}</span>
                <span>{_t('unit_per_night')}</span>
              </div>
            )}
            {minStayText && <div className="booking-sticky-bar-sub">{minStayText}</div>}
          </>
        )}

        {state === 'loading' && (
          <>
            {/* Purely visual — the announcement is the status region above. */}
            <div className="booking-sticky-bar-skeleton" aria-hidden="true" />
            {datesLine}
          </>
        )}

        {state === 'quoted' && (
          <>
            {showPriceDetails ? (
              <button type="button" className="booking-sticky-bar-price" onClick={onPriceClick} ref={priceRef}>
                {priceLine}
              </button>
            ) : (
              <span className="booking-sticky-bar-price">{priceLine}</span>
            )}
            {datesLine}
          </>
        )}

        {state === 'error' && (
          <>
            {errorText && <div className="booking-sticky-bar-error">{errorText}</div>}
            {datesLine}
          </>
        )}
      </div>
      <button
        type="button"
        className={`checkout-button booking-sticky-bar-button${buttonClassName ? ` ${buttonClassName}` : ''}`}
        disabled={buttonDisabled}
        onClick={onButtonClick}
      >
        {buttonText}
      </button>
    </div>
  )
}
